import { useMemo, useState } from "react";
import { Box, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { colors } from "../../../../theme";
import { TAXONOMY_PATHS, humanizeTaxonomy } from "./assetClassification";

// One tree built once from TAXONOMY_PATHS (asset_type[/subcategory[/type]]
// strings from the canonical taxonomy in assetClassification.js) — the same
// source of truth the icon/label resolution already uses, so a value picked
// here can never drift from what the rest of the app recognizes.
const buildTree = () => {
  const root = { path: "", children: new Map() };
  TAXONOMY_PATHS.forEach((path) => {
    const parts = path.split("/");
    let node = root;
    parts.forEach((part, i) => {
      const partial = parts.slice(0, i + 1).join("/");
      if (!node.children.has(part)) node.children.set(part, { value: part, path: partial, children: new Map() });
      node = node.children.get(part);
    });
  });
  return root;
};
const TREE = buildTree();

const nodeMatches = (node, query) => {
  if (!query) return true;
  if (humanizeTaxonomy(node.value).toLowerCase().includes(query)) return true;
  for (const child of node.children.values()) if (nodeMatches(child, query)) return true;
  return false;
};

// A search hit deep in the tree is useless if its ancestors stay collapsed —
// during a search every branch that contains a match forces itself open
// (mirrors the pattern MibPanel's value tree uses for the same reason),
// independent of whatever the user had manually expanded/collapsed before.
const TreeNode = ({ node, depth, query, selectedPath, onSelect }) => {
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = node.children.size > 0;
  const isOpen = query ? true : expanded;
  const isSelected = node.path === selectedPath;
  const visibleChildren = useMemo(
    () => [...node.children.values()].filter((c) => nodeMatches(c, query)),
    [node, query]
  );

  return (
    <Box>
      <Box
        onClick={() => onSelect(node.path)}
        sx={{
          display: "flex", alignItems: "center", gap: 0.4, pl: depth * 2, py: 0.35, borderRadius: 1, cursor: "pointer",
          bgcolor: isSelected ? "rgba(69,144,149,0.18)" : "transparent",
          "&:hover": { bgcolor: isSelected ? "rgba(69,144,149,0.22)" : "rgba(255,255,255,0.05)" },
        }}
      >
        {hasChildren ? (
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }} sx={{ p: 0.2 }}>
            {isOpen ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 22, flexShrink: 0 }} />
        )}
        <Typography sx={{ fontSize: 12.5, color: isSelected ? "#7de8ed" : colors.grey[200], fontWeight: isSelected ? 700 : 400 }}>
          {humanizeTaxonomy(node.value)}
        </Typography>
      </Box>
      {hasChildren && isOpen && (
        <Box>
          {visibleChildren.map((child) => (
            <TreeNode key={child.path} node={child} depth={depth + 1} query={query} selectedPath={selectedPath} onSelect={onSelect} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// A searchable, filterable tree over the canonical asset_type › subcategory ›
// type taxonomy. Any level can be picked as the final selection — the
// taxonomy itself says subcategory/type are optional, not just "not yet
// expanded" (see assetClassification.js's own header comment) — so clicking
// a branch node (not only a leaf) is a valid, final choice.
// `value` is the currently selected "asset_type[/subcategory[/type]]" path
// (or "" for none). `onChange(path)` fires with the newly clicked node's path.
const ClassificationTreePicker = ({ value, onChange, maxHeight = 260 }) => {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const roots = useMemo(
    () => [...TREE.children.values()].filter((n) => nodeMatches(n, query)),
    [query]
  );

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Search classification…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: colors.grey[500] }} /></InputAdornment> } }}
        sx={{ mb: 1 }}
      />
      <Box sx={{ maxHeight, overflowY: "auto", border: `1px solid ${colors.grey[800]}`, borderRadius: 1, p: 0.5 }}>
        {roots.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: colors.grey[500], p: 1, fontStyle: "italic" }}>No classification matches "{search}".</Typography>
        ) : (
          roots.map((node) => (
            <TreeNode key={node.path} node={node} depth={0} query={query} selectedPath={value} onSelect={onChange} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default ClassificationTreePicker;
